import { useState } from "react";
import api, { useList, errMsg } from "@/lib/api";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, X, GraduationCap } from "lucide-react";

export default function AdminTeachingRequestsPage() {
  const { data: requests, loading, reload } = useList("/admin/teaching-requests");
  const [target, setTarget] = useState(null); // {row, decision}
  const [adminNote, setAdminNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return <Spinner full />;
  const pending = (requests || []).filter((r) => r.status === "pending");
  const decided = (requests || []).filter((r) => r.status !== "pending");

  const decide = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/teaching-requests/${target.row.id}/decide`, {
        decision: target.decision,
        admin_note: adminNote || null,
      });
      toast.success(`Request ${target.decision === "approve" ? "approved" : "rejected"}`);
      setTarget(null); setAdminNote("");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-teaching-requests-page">
      <PageHeader title="Teaching requests" description="Teachers requesting to be approved for additional courses/grades." />

      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-amber-800">
        <GraduationCap className="h-4 w-4" />Pending ({pending.length})
      </h3>
      {pending.length === 0
        ? <div className="mb-8"><EmptyState text="No pending requests. Teachers can submit from their portal." /></div>
        : <div className="space-y-2 mb-8" data-testid="pending-requests">
            {pending.map((r) => (
              <div key={r.id} className="rounded-lg border bg-card p-4 flex flex-col sm:flex-row sm:items-center gap-4"
                   data-testid={`admin-request-row-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{r.teacher_name} → {r.course_name}</p>
                  {r.note && <p className="text-xs text-muted-foreground mt-0.5">Teacher note: {r.note}</p>}
                  <p className="text-[10px] text-muted-foreground mt-1">{r.created_at?.slice(0, 10)}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" onClick={() => setTarget({ row: r, decision: "approve" })}
                          data-testid={`approve-request-${r.id}`}>
                    <Check className="h-4 w-4 mr-1" />Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setTarget({ row: r, decision: "reject" })}
                          data-testid={`reject-request-${r.id}`}>
                    <X className="h-4 w-4 mr-1" />Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>}

      <h3 className="text-sm font-semibold mb-3 text-muted-foreground">Decided ({decided.length})</h3>
      {decided.length === 0
        ? <EmptyState text="No decided requests yet." />
        : <div className="space-y-2" data-testid="decided-requests">
            {decided.map((r) => (
              <div key={r.id} className="rounded-md border p-3 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm">{r.teacher_name} → {r.course_name}</p>
                  {r.admin_note && <p className="text-xs text-muted-foreground mt-0.5">Admin: {r.admin_note}</p>}
                  <p className="text-[10px] text-muted-foreground mt-1">{r.decided_at?.slice(0, 10)}</p>
                </div>
                <StatusBadge status={r.status} />
              </div>
            ))}
          </div>}

      <Dialog open={!!target} onOpenChange={(o) => !o && (setTarget(null), setAdminNote(""))}>
        <DialogContent data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle>{target?.decision === "approve" ? "Approve" : "Reject"} request</DialogTitle>
            <DialogDescription>
              {target?.row.teacher_name} → {target?.row.course_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">Note for the teacher (optional)</Label>
            <Textarea rows={3} value={adminNote} onChange={(e) => setAdminNote(e.target.value)}
              data-testid="decision-note" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setTarget(null)}>Cancel</Button>
            <Button onClick={decide} disabled={busy} data-testid="decision-confirm">
              {busy ? "Saving…" : (target?.decision === "approve" ? "Confirm approve" : "Confirm reject")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
